import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
    // Waste & recycling
    faTrashCan, faTrash, faDumpster, faDumpsterFire, faRecycle, faBottleWater, faWineBottle, faBoxArchive,
    faOilCan, faBatteryFull, faBiohazard, faRadiation, faSkullCrossbones,
    // Cleaning
    faBroom, faSoap, faSprayCanSparkles, faPumpSoap, faHandSparkles, faBucket, faSprayCan,
    faToiletPaper, faSink, faBath, faShower, faToilet, faWater, faDroplet, faFaucet, faFaucetDrip,
    faSnowflake, faTemperatureHigh, faFire, faWind, faSun,
    // Buildings & rooms
    faBuilding, faHouse, faHouseChimney, faWarehouse, faIndustry, faStore, faSchool, faHospital, faHotel,
    faChurch, faCity, faDoorOpen, faDoorClosed, faStairs, faElevator, faWindowMaximize, faWindowRestore,
    faCouch, faBed, faChair, faKitchenSet, faUtensils, faRestroom, faTent,
    // Access
    faKey, faLock, faLockOpen, faShieldHalved, faBell, faIdCard, faCircleExclamation, faTriangleExclamation,
    faBan, faPhone, faSquareParking,
    // Vehicles & transport
    faTruck, faTruckPickup, faTruckMoving, faTruckFront, faVanShuttle, faCar, faTrailer, faTractor, faDolly,
    faCartFlatbed, faPallet, faRoute, faMapLocationDot, faLocationDot, faGasPump, faChargingStation,
    // Tools & maintenance
    faWrench, faScrewdriverWrench, faHammer, faToolbox, faGears, faGear, faPlug, faBolt, faLightbulb,
    faFan, faPaintRoller, faPaintbrush, faRulerCombined, faHelmetSafety, faFireExtinguisher,
    faFilter, faTachographDigital, faGauge, faStopwatch, faClock, faCalendarDays,
    // Garden & outdoor
    faLeaf, faTree, faSeedling, faSpa, faMountain, faCloudRain, faUmbrella, faIgloo, faPersonDigging,
    faTrowel, faWheatAwn, faFeather, faPaw, faBug,
    // Items & packaging
    faBox, faBoxOpen, faBoxesStacked, faCube, faCubes, faBagShopping, faSuitcase, faBasketShopping,
    faCartShopping, faGift, faTag, faTags, faBarcode, faClipboardList, faClipboardCheck, faFileLines,
    faReceipt, faScaleBalanced, faWeightHanging, faLayerGroup, faPrescriptionBottle, faJar, faFlask,
    // People & service
    faUser, faUsers, faPeopleCarryBox, faPersonWalking, faHandshake, faHandHoldingDroplet, faHeadset,
    faStar, faHeart, faCircleCheck, faCircle, faSquare, faFlag, faThumbsUp, faCrown, faMedal,
} from '@fortawesome/free-solid-svg-icons';

/**
 * Hand-picked Font Awesome icons a product can use (product setting "taskIcon").
 * Stored by Font Awesome name (e.g. "trash-can"), so adding icons is safe; never
 * remove one that products may already use — it would just show no icon.
 *
 * Kept small on purpose: the full solid set is ~1 MB (~310 KB gzipped); this list
 * ships with the app so icons show instantly on the crew's phones.
 */
export type ProductIconCategory =
    "waste" | "cleaning" | "buildings" | "access" | "vehicles" | "tools" | "outdoor" | "items" | "people";

export type ProductIconEntry = {
    icon: IconDefinition;
    category: ProductIconCategory;
    /** Extra search words (English + Swedish) besides the icon's own name. */
    words: string;
};

const e = (icon: IconDefinition, category: ProductIconCategory, words: string): ProductIconEntry =>
    ({ icon, category, words });

export const PRODUCT_ICON_CATEGORIES: ProductIconCategory[] =
    ["waste", "cleaning", "buildings", "access", "vehicles", "tools", "outdoor", "items", "people"];

const ENTRIES: ProductIconEntry[] = [
    // Waste & recycling
    e(faTrashCan, "waste", "bin garbage waste kärl sopkärl soptunna sopor avfall"),
    e(faTrash, "waste", "bin garbage waste kärl soptunna sopor avfall"),
    e(faDumpster, "waste", "container skip bin container sopcontainer storkärl avfall"),
    e(faDumpsterFire, "waste", "container fire brand container"),
    e(faRecycle, "waste", "recycling återvinning sortering källsortering"),
    e(faBottleWater, "waste", "bottle pet flaska pant plast"),
    e(faWineBottle, "waste", "glass bottle glas flaska glasigloo"),
    e(faBoxArchive, "waste", "cardboard papper kartong wellpapp arkiv"),
    e(faOilCan, "waste", "oil olja farligt avfall"),
    e(faBatteryFull, "waste", "battery batteri elavfall"),
    e(faBiohazard, "waste", "hazard farligt avfall smitta"),
    e(faRadiation, "waste", "hazard farligt strålning"),
    e(faSkullCrossbones, "waste", "poison gift farligt avfall"),
    // Cleaning
    e(faBroom, "cleaning", "broom sweep room kvast sopa städ rum miljörum"),
    e(faSoap, "cleaning", "soap wash tvål tvätt rengöring"),
    e(faSprayCanSparkles, "cleaning", "spray clean sanering desinfektion rengöring"),
    e(faPumpSoap, "cleaning", "soap pump tvål tvättmedel"),
    e(faHandSparkles, "cleaning", "clean hygiene hygien ren"),
    e(faBucket, "cleaning", "bucket hink"),
    e(faSprayCan, "cleaning", "spray sprej klotter sanering"),
    e(faToiletPaper, "cleaning", "toilet paper toalettpapper förbrukning"),
    e(faSink, "cleaning", "sink diskbänk handfat kök"),
    e(faBath, "cleaning", "bathroom badrum badkar"),
    e(faShower, "cleaning", "shower dusch badrum"),
    e(faToilet, "cleaning", "toilet toalett wc"),
    e(faWater, "cleaning", "water vatten tvätt högtryck"),
    e(faDroplet, "cleaning", "water drop vatten droppe"),
    e(faFaucet, "cleaning", "tap kran vatten"),
    e(faFaucetDrip, "cleaning", "tap leak kran läcka"),
    e(faSnowflake, "cleaning", "snow winter snö vinter snöröjning"),
    e(faTemperatureHigh, "cleaning", "hot water varmvatten temperatur"),
    e(faFire, "cleaning", "fire heat eld värme"),
    e(faWind, "cleaning", "air dry luft torkning ventilation"),
    e(faSun, "cleaning", "sun summer sol sommar"),
    // Buildings & rooms
    e(faBuilding, "buildings", "building apartment byggnad hus fastighet lägenhet"),
    e(faHouse, "buildings", "house villa hus hem"),
    e(faHouseChimney, "buildings", "house villa hus skorsten"),
    e(faWarehouse, "buildings", "warehouse lager förråd garage"),
    e(faIndustry, "buildings", "factory industri fabrik"),
    e(faStore, "buildings", "shop store butik affär"),
    e(faSchool, "buildings", "school skola förskola"),
    e(faHospital, "buildings", "hospital sjukhus vård"),
    e(faHotel, "buildings", "hotel hotell"),
    e(faChurch, "buildings", "church kyrka"),
    e(faCity, "buildings", "city stad område"),
    e(faDoorOpen, "buildings", "door room dörr rum miljörum soprum"),
    e(faDoorClosed, "buildings", "door room dörr rum soprum"),
    e(faStairs, "buildings", "stairs trappa trapphus"),
    e(faElevator, "buildings", "elevator hiss"),
    e(faWindowMaximize, "buildings", "window fönster fönsterputs"),
    e(faWindowRestore, "buildings", "windows fönster"),
    e(faCouch, "buildings", "sofa soffa möbler"),
    e(faBed, "buildings", "bed säng"),
    e(faChair, "buildings", "chair stol"),
    e(faKitchenSet, "buildings", "kitchen kök"),
    e(faUtensils, "buildings", "restaurant food restaurang mat kök"),
    e(faRestroom, "buildings", "restroom toalett wc"),
    e(faTent, "buildings", "tent tält event"),
    // Access
    e(faKey, "access", "key nyckel kod"),
    e(faLock, "access", "lock låst lås"),
    e(faLockOpen, "access", "unlock öppen olåst"),
    e(faShieldHalved, "access", "security säkerhet"),
    e(faBell, "access", "bell ring ringa ringklocka"),
    e(faIdCard, "access", "card badge tagg passerkort bricka"),
    e(faCircleExclamation, "access", "important viktigt"),
    e(faTriangleExclamation, "access", "warning varning"),
    e(faBan, "access", "no stop nej förbjudet"),
    e(faPhone, "access", "phone call telefon ringa"),
    e(faSquareParking, "access", "parking parkering"),
    // Vehicles & transport
    e(faTruck, "vehicles", "truck lastbil bil"),
    e(faTruckPickup, "vehicles", "pickup bil"),
    e(faTruckMoving, "vehicles", "moving flytt lastbil"),
    e(faTruckFront, "vehicles", "truck lastbil"),
    e(faVanShuttle, "vehicles", "van skåpbil"),
    e(faCar, "vehicles", "car bil"),
    e(faTrailer, "vehicles", "trailer släp släpvagn"),
    e(faTractor, "vehicles", "tractor traktor"),
    e(faDolly, "vehicles", "dolly kärra säckkärra"),
    e(faCartFlatbed, "vehicles", "trolley vagn transport"),
    e(faPallet, "vehicles", "pallet pall"),
    e(faRoute, "vehicles", "route rutt körning"),
    e(faMapLocationDot, "vehicles", "map karta plats"),
    e(faLocationDot, "vehicles", "location plats adress"),
    e(faGasPump, "vehicles", "fuel bränsle diesel"),
    e(faChargingStation, "vehicles", "charging laddning el"),
    // Tools & maintenance
    e(faWrench, "tools", "wrench service verktyg skiftnyckel"),
    e(faScrewdriverWrench, "tools", "tools repair verktyg reparation"),
    e(faHammer, "tools", "hammer hammare"),
    e(faToolbox, "tools", "toolbox verktygslåda"),
    e(faGears, "tools", "gears maskin service"),
    e(faGear, "tools", "gear inställning service"),
    e(faPlug, "tools", "plug el kontakt"),
    e(faBolt, "tools", "electric el ström"),
    e(faLightbulb, "tools", "light bulb lampa belysning"),
    e(faFan, "tools", "fan fläkt ventilation"),
    e(faPaintRoller, "tools", "paint måla målning"),
    e(faPaintbrush, "tools", "paint pensel måla"),
    e(faRulerCombined, "tools", "measure mäta"),
    e(faHelmetSafety, "tools", "helmet hjälm skydd"),
    e(faFireExtinguisher, "tools", "extinguisher brandsläckare"),
    e(faFilter, "tools", "filter filter"),
    e(faTachographDigital, "tools", "meter mätare"),
    e(faGauge, "tools", "gauge mätare tryck"),
    e(faStopwatch, "tools", "time hour tid timme"),
    e(faClock, "tools", "time hour tid timme klocka"),
    e(faCalendarDays, "tools", "calendar kalender"),
    // Garden & outdoor
    e(faLeaf, "outdoor", "leaf garden löv trädgård"),
    e(faTree, "outdoor", "tree träd trädgård"),
    e(faSeedling, "outdoor", "plant planta grönt"),
    e(faSpa, "outdoor", "flower blomma"),
    e(faMountain, "outdoor", "gravel grus sten"),
    e(faCloudRain, "outdoor", "rain regn dagvatten"),
    e(faUmbrella, "outdoor", "umbrella paraply"),
    e(faIgloo, "outdoor", "igloo glasigloo behållare"),
    e(faPersonDigging, "outdoor", "digging gräva"),
    e(faTrowel, "outdoor", "trowel murslev"),
    e(faWheatAwn, "outdoor", "grass gräs"),
    e(faFeather, "outdoor", "bird fågel"),
    e(faPaw, "outdoor", "animal djur"),
    e(faBug, "outdoor", "pest skadedjur insekt"),
    // Items & packaging
    e(faBox, "items", "box låda kartong"),
    e(faBoxOpen, "items", "box låda öppen"),
    e(faBoxesStacked, "items", "boxes lådor lager"),
    e(faCube, "items", "item artikel"),
    e(faCubes, "items", "items artiklar"),
    e(faBagShopping, "items", "bag påse säck"),
    e(faSuitcase, "items", "case väska"),
    e(faBasketShopping, "items", "basket korg"),
    e(faCartShopping, "items", "cart vagn"),
    e(faGift, "items", "gift present"),
    e(faTag, "items", "tag etikett"),
    e(faTags, "items", "tags etiketter"),
    e(faBarcode, "items", "barcode streckkod"),
    e(faClipboardList, "items", "list lista checklista"),
    e(faClipboardCheck, "items", "check kontroll besiktning"),
    e(faFileLines, "items", "document dokument rapport"),
    e(faReceipt, "items", "receipt kvitto avgift"),
    e(faScaleBalanced, "items", "scale våg vikt"),
    e(faWeightHanging, "items", "weight vikt kg"),
    e(faLayerGroup, "items", "layers lager"),
    e(faPrescriptionBottle, "items", "bottle flaska"),
    e(faJar, "items", "jar burk"),
    e(faFlask, "items", "chemical kemikalie"),
    // People & service
    e(faUser, "people", "person person"),
    e(faUsers, "people", "people personer"),
    e(faPeopleCarryBox, "people", "carry bära flytt"),
    e(faPersonWalking, "people", "walk gå"),
    e(faHandshake, "people", "deal avtal"),
    e(faHandHoldingDroplet, "people", "service tjänst"),
    e(faHeadset, "people", "support"),
    e(faStar, "people", "star stjärna"),
    e(faHeart, "people", "heart hjärta"),
    e(faCircleCheck, "people", "done klar"),
    e(faCircle, "people", "circle cirkel"),
    e(faSquare, "people", "square fyrkant"),
    e(faFlag, "people", "flag flagga"),
    e(faThumbsUp, "people", "ok bra"),
    e(faCrown, "people", "premium"),
    e(faMedal, "people", "medal medalj"),
];

/** One entry per Font Awesome name (aliases like faTrashAlt resolve to the same icon). */
export const PRODUCT_ICONS: ProductIconEntry[] = ENTRIES.filter(
    (entry, i) => ENTRIES.findIndex(other => other.icon.iconName === entry.icon.iconName) === i);

const BY_NAME = new Map<string, IconDefinition>(PRODUCT_ICONS.map(entry => [entry.icon.iconName, entry.icon]));

/** The icon for a stored name, or null when none is set (or the name is unknown). */
export const productIcon = (name: string | null | undefined): IconDefinition | null =>
    name ? BY_NAME.get(name) ?? null : null;

export const searchProductIcons = (query: string, category: ProductIconCategory | null): ProductIconEntry[] => {
    const q = query.trim().toLowerCase();
    return PRODUCT_ICONS.filter(entry =>
        (!category || entry.category === category)
        && (!q || entry.icon.iconName.includes(q) || entry.words.toLowerCase().includes(q)));
};
